import { CropSelectorState, SelectorCropTNode } from "./types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { dashboardFetchReq, MethodsType, ResponseTransformer } from "@api/dashboards";
import logger from "@utils/logger";
import { applyExtraReducer } from "../../../weather";
import { TryServerResponseRequest } from "../../../auth";
import { dashboardURL } from "@utils/utils";
import { AxiosRequestConfig } from "axios";
import { RequiredFields } from "@utils/typeUtils";
import { intersectionWith, isEmpty, size } from "lodash";

export type ModifiedAxiosRequestConfig<B> = RequiredFields<AxiosRequestConfig<B>, 'url'>

const initialState: CropSelectorState = {
    data: [],
    isApiLoading: false,
    apiError: undefined,
    selected: [],
};
const log = logger.getLogger("redux.thunk")

type CreateThunkOptions<Q, R, Z> = {
    url: string,
    prefix?: string,
    params?: Partial<Q>,
    transformer?: ResponseTransformer<R, Z>
    method?: MethodsType
}

export type DashboardReqBody<T = {}> = {
    cropRotationIds: string[]
} & T

export type DashboardReqBodyWithDate<T = {}> = DashboardReqBody<{ date: number } & T>

type PayloadConverter<P, B> = (payload: P, url: string, params?: Partial<P>) => ModifiedAxiosRequestConfig<B>

const defaultPayloadConverter = <P, B>(payload: P, url: string): ModifiedAxiosRequestConfig<B> => {
    return { url: url }
}

/*
* R - Response type generic
* P - Payload type generic (DashboardQueryParams in mostly cases)
* B - Body type generic (specified for payloadConverter)
* PayloadConvertor - allows you to change the arrangement of parameters for a request
* */
export const createThunk = <R, P = {}, B = {}>() =>
    <Z = R>(options: CreateThunkOptions<P, R, Z>, payloadConverter: PayloadConverter<P, B> = defaultPayloadConverter) =>
        createAsyncThunk(
            options.url,
            async (payload: P, thunkAPI) => {
                const convertedPayload =
                    payloadConverter(payload, options.url, options.params);

                return TryServerResponseRequest(() => {
                    return dashboardFetchReq<R, Z, B>(options.transformer, thunkAPI)({method: options.method || "GET", ...convertedPayload })
                }, thunkAPI)
            }
        )

type FetchCropsQueryParams = {
    date: number
}

const fetchCrops = 
    createThunk<SelectorCropTNode[], FetchCropsQueryParams>()(
        { url: dashboardURL('crops') },
        (params, url) => ({
            url,
            method: 'POST',
            data: {
                date: params.date
            }
        })
    )

const cropSelectorSlice = createSlice({
    name: 'cropSelectorSlice',
    initialState,
    reducers: {
        setCurrentCrop(state: CropSelectorState, action: PayloadAction<SelectorCropTNode[] | undefined>) {
            state.selected = action.payload
        },
    },
    extraReducers: (builder) => {
        applyExtraReducer(builder, fetchCrops, {
            fulfillReducer(state, action){
                const lastPickedCrops = intersectionWith(
                    action.payload || [],
                    state.selected || [],
                    (crop1, crop2) => crop1.id === crop2.id
                )

                const areAllCropsWereSelected = size(state.selected || []) === size(state.data)

                state.data = action.payload
                state.selected = isEmpty(lastPickedCrops) || areAllCropsWereSelected ? action.payload : lastPickedCrops
            }
        })
    }
})

export const actions = { ...cropSelectorSlice.actions, fetchCrops }
export default cropSelectorSlice.reducer